/** Tab order indices for pharmacy page (positive tabindex, cards use CARD_START + index). */
export const PHARMACY_TAB_INDEX = {
  CARD_START: 1,
  TYPE: 100,
  FORM: 101,
  DOSAGE: 102,
  QUANTITY: 103,
  ZIP: 104,
  UPDATE: 105,
  LOCATE: 106,
  SORT: 107,
  SEARCH: 108,
} as const;

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface SavedTabIndex {
  element: HTMLElement;
  tabIndex: number;
}

let savedBackgroundTabIndices: SavedTabIndex[] = [];
let pendingFocusTrapTimeout: ReturnType<typeof setTimeout> | null = null;
let keyboardFocusOutlineInitialized = false;

/** Show .tab-focus outline only after Tab navigation, not mouse/touch click. */
export function initKeyboardFocusOutline(): void {
  if (keyboardFocusOutlineInitialized || typeof document === 'undefined') {
    return;
  }
  keyboardFocusOutlineInitialized = true;

  let keyboardMode = false;

  window.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Tab') {
        keyboardMode = true;
      }
    },
    true,
  );

  const disableKeyboardMode = (): void => {
    keyboardMode = false;
  };

  window.addEventListener('mousedown', disableKeyboardMode, true);
  window.addEventListener('pointerdown', disableKeyboardMode, true);
  window.addEventListener('touchstart', disableKeyboardMode, true);

  document.addEventListener(
    'focusin',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || !target.classList.contains('tab-focus')) {
        return;
      }
      target.classList.toggle('keyboard-focus', keyboardMode);
    },
    true,
  );

  document.addEventListener(
    'focusout',
    (event) => {
      const target = event.target;
      if (target instanceof HTMLElement) {
        target.classList.remove('keyboard-focus');
      }
    },
    true,
  );
}

export function cancelPendingFocusTrap(): void {
  if (pendingFocusTrapTimeout !== null) {
    clearTimeout(pendingFocusTrapTimeout);
    pendingFocusTrapTimeout = null;
  }
}

function isVisible(el: HTMLElement): boolean {
  return el.getClientRects().length > 0;
}

export function isDisabledTabTarget(el: HTMLElement): boolean {
  if (el.hasAttribute('disabled') || el.getAttribute('aria-disabled') === 'true') {
    return true;
  }
  return !!el.closest('.singleDoseForm, .sortby_ddl.inactive');
}

export function getFocusTrapStops(container: HTMLElement, attrName: string): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(`[${attrName}]`))
    .filter(
      (el) =>
        el.tabIndex >= 0 &&
        !el.hasAttribute('disabled') &&
        isVisible(el) &&
        !isDisabledTabTarget(el),
    )
    .sort((a, b) => a.tabIndex - b.tabIndex);
}

/** Focusable, visible tab stops inside a container (popup panel). */
export function getTabStops(container: HTMLElement): HTMLElement[] {
  return getFocusTrapStops(container, 'data-popup-tab');
}

export function activateClickTarget(event: KeyboardEvent): void {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }
  const target = event.currentTarget as HTMLElement;
  if (isDisabledTabTarget(target)) {
    return;
  }
  event.preventDefault();
  target.click();
}

/** Remove every focusable element outside the modal from the tab order. */
export function trapBackgroundFocus(modalRoot: HTMLElement): void {
  releaseBackgroundFocus();

  const focusable = Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  for (const el of focusable) {
    if (modalRoot.contains(el)) {
      continue;
    }
    savedBackgroundTabIndices.push({ element: el, tabIndex: el.tabIndex });
    el.tabIndex = -1;
  }
}

/** Restore tab order for background elements after the modal closes. */
export function releaseBackgroundFocus(): void {
  cancelPendingFocusTrap();
  for (const { element, tabIndex } of savedBackgroundTabIndices) {
    if (document.body.contains(element)) {
      element.tabIndex = tabIndex;
    }
  }
  savedBackgroundTabIndices = [];
}

export function trapTabWithinContainer(
  event: KeyboardEvent,
  container: HTMLElement,
  stops = getTabStops(container),
): void {
  if (event.key !== 'Tab' || stops.length === 0) {
    return;
  }

  const active = document.activeElement as HTMLElement;
  const index = stops.indexOf(active);

  if (!container.contains(active)) {
    return;
  }

  if (event.shiftKey) {
    event.preventDefault();
    if (index <= 0) {
      stops[stops.length - 1].focus();
    } else {
      stops[index - 1].focus();
    }
    return;
  }

  event.preventDefault();
  if (index === -1 || index === stops.length - 1) {
    stops[0].focus();
  } else {
    stops[index + 1].focus();
  }
}

export function focusFirstPopupTab(container: HTMLElement): void {
  getTabStops(container)[0]?.focus();
}

export function activatePopupFocusTrap(
  containerSelector: string,
  initialFocusSelector?: string,
): void {
  activateFocusTrap(containerSelector, 'data-popup-tab', initialFocusSelector);
}

export function activateFocusTrap(
  containerSelector: string,
  tabAttr: string,
  initialFocusSelector?: string,
): void {
  cancelPendingFocusTrap();
  pendingFocusTrapTimeout = setTimeout(() => {
    pendingFocusTrapTimeout = null;
    const container = document.querySelector(containerSelector);
    if (!(container instanceof HTMLElement)) {
      return;
    }
    if (initialFocusSelector) {
      container.querySelector<HTMLElement>(initialFocusSelector)?.focus();
      return;
    }
    getFocusTrapStops(container, tabAttr)[0]?.focus();
  });
}

/** Re-apply pharmacy page tabindex after navigation (clears stale popup trap state). */
export function enforcePharmacyTabOrder(enableSearchTab = false): void {
  releaseBackgroundFocus();

  document.querySelectorAll<HTMLElement>('app-pharmacy .pharmacy-tab-card').forEach((card, index) => {
    card.tabIndex = PHARMACY_TAB_INDEX.CARD_START + index;
  });

  const sidebarTabStops: Array<[string, number]> = [
    ['app-pharmacy .pharmacy-tab-type', PHARMACY_TAB_INDEX.TYPE],
    ['app-pharmacy .pharmacy-tab-form', PHARMACY_TAB_INDEX.FORM],
    ['app-pharmacy .pharmacy-tab-dosage', PHARMACY_TAB_INDEX.DOSAGE],
    ['app-pharmacy .pharmacy-tab-quantity', PHARMACY_TAB_INDEX.QUANTITY],
    ['app-pharmacy .pharmacy-tab-zip', PHARMACY_TAB_INDEX.ZIP],
    ['app-pharmacy .pharmacy-tab-update', PHARMACY_TAB_INDEX.UPDATE],
    ['app-pharmacy .pharmacy-tab-locate', PHARMACY_TAB_INDEX.LOCATE],
    ['app-pharmacy .pharmacy-tab-sort', PHARMACY_TAB_INDEX.SORT],
  ];

  for (const [selector, tabIndex] of sidebarTabStops) {
    const el = document.querySelector<HTMLElement>(selector);
    if (!el || isDisabledTabTarget(el)) {
      continue;
    }
    el.tabIndex = tabIndex;
  }

  const loadMore = document.querySelector<HTMLElement>('app-pharmacy .pharmacy-list-footer .loadmore');
  if (loadMore instanceof HTMLElement && loadMore.tabIndex >= 0) {
    const cards = document.querySelectorAll('app-pharmacy .pharmacy-tab-card').length;
    loadMore.tabIndex = PHARMACY_TAB_INDEX.CARD_START + cards;
  }

  const searchInput = document.getElementById('SearchDrugText');
  if (searchInput instanceof HTMLElement) {
    searchInput.tabIndex = enableSearchTab ? PHARMACY_TAB_INDEX.SEARCH : -1;
    if (!enableSearchTab) {
      searchInput.blur();
    }
  }
}

export function focusFirstPharmacyTabStop(): boolean {
  blurPharmacySearchInput();
  if (focusFirstPharmacyCardIfReady()) {
    return true;
  }
  const sidebar =
    document.querySelector<HTMLElement>('app-pharmacy .pharmacy-tab-type') ??
    document.querySelector<HTMLElement>('app-pharmacy .pharmacy-tab-quantity');
  if (sidebar instanceof HTMLElement && !isDisabledTabTarget(sidebar)) {
    sidebar.focus();
    return true;
  }
  return false;
}

export function blurPharmacySearchInput(): void {
  document.getElementById('SearchDrugText')?.blur();
}

export function focusFirstPharmacyCardIfReady(): boolean {
  blurPharmacySearchInput();
  const firstCard = document.getElementById('pharmacy-card-0');
  if (firstCard instanceof HTMLElement && isVisible(firstCard)) {
    firstCard.focus();
    return true;
  }
  return false;
}
